/**
 * One workspace on the Discover Workspaces page (Figma "Card-Projects"
 * 10219:24465 and siblings).  Grid cards are 389px; the list view renders the
 * same content as a full-width row (`layout="list"`).
 *
 * Top row: 60px logo (image, or the initial on a tinted disc), name +
 * "Created on <date>", and the top-right slot: the caller's role tag when a
 * member, otherwise "Ask to join" / "Cancel request".  Bottom row: "Members"
 * with the avatar stack.  Clicking anywhere else on the card opens the
 * Workspace Members modal.
 */
import React from 'react';
import { Button, TagColored, Text } from '@spark';
import { formatLongDate } from '@/features/billing/utils/billingFormat';
import MemberAvatarStack from './MemberAvatarStack';
import { displayRole, orderMembersForStack } from '../../utils/discoverFilters';

export const ASK_TO_JOIN_LABEL = 'Ask to join';
export const CANCEL_REQUEST_LABEL = 'Cancel request';

/** Figma tag colours for the discover surfaces: Owner is volcano here (10219:25323). */
export const DISCOVER_ROLE_TAG_VARIANT = {
    Owner: 'volcano',
    Admin: 'purple',
    Editor: 'pink',
    Viewer: 'cyan',
};

/** The two tinted discs the design uses for logo-less workspaces (10225:26091 / 10225:26094). */
const LOGO_TINTS = ['peach', 'magenta'];

export function logoTint(name) {
    const text = String(name || '');
    let hash = 0;
    for (let i = 0; i < text.length; i += 1) hash = (hash * 31 + text.charCodeAt(i)) % 65536;
    return LOGO_TINTS[hash % LOGO_TINTS.length];
}

export function WorkspaceLogo({ name, logo }) {
    const label = String(name || 'Workspace').trim() || 'Workspace';
    if (logo) {
        return <img className="discover-card__logo discover-card__logo--image" src={logo} alt="" />;
    }
    return (
        <span className={`discover-card__logo discover-card__logo--${logoTint(label)}`} aria-hidden="true">
            {label.charAt(0).toUpperCase()}
        </span>
    );
}

export function createdOnLabel(createdAt) {
    const date = formatLongDate(createdAt);
    return date ? `Created on ${date}` : '';
}

export default function WorkspaceDiscoverCard({
    workspace,
    people = {},
    highlightEmail = '',
    layout = 'grid',
    busy = false,
    onOpenMembers,
    onAskToJoin,
    onCancelRequest,
}) {
    const role = displayRole(workspace);
    const pending = Boolean(workspace.join_request);
    const createdOn = createdOnLabel(workspace.created_at);
    const stackEmails = orderMembersForStack(workspace.member_emails, highlightEmail);

    const open = () => onOpenMembers?.(workspace);
    const handleKeyDown = (event) => {
        if (event.target !== event.currentTarget) return;
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            open();
        }
    };
    const stop = (handler) => (event) => {
        event.stopPropagation();
        handler?.(workspace);
    };

    let action = null;
    if (role) {
        action = (
            <TagColored
                className="discover-card__role"
                variant={DISCOVER_ROLE_TAG_VARIANT[role] || 'yellow'}
                label={role}
            />
        );
    } else if (pending) {
        action = (
            <Button
                size="tiny"
                variant="secondary"
                className="discover-card__action"
                disabled={busy}
                onClick={stop(onCancelRequest)}
                data-testid="discover-card-cancel"
            >
                {CANCEL_REQUEST_LABEL}
            </Button>
        );
    } else {
        action = (
            <Button
                size="tiny"
                variant="tertiary"
                className="discover-card__action"
                disabled={busy}
                onClick={stop(onAskToJoin)}
                data-testid="discover-card-ask"
            >
                {ASK_TO_JOIN_LABEL}
            </Button>
        );
    }

    return (
        <div
            className={`discover-card discover-card--${layout}`}
            role="button"
            tabIndex={0}
            aria-label={`${workspace.name} members`}
            onClick={open}
            onKeyDown={handleKeyDown}
            data-testid="discover-card"
            data-workspace-id={workspace.id}
        >
            <div className="discover-card__identity">
                <WorkspaceLogo name={workspace.name} logo={workspace.logo} />
                <div className="discover-card__title">
                    <Text as="p" variant="lead-medium" className="discover-card__name" title={workspace.name}>
                        {workspace.name}
                    </Text>
                    {createdOn ? (
                        <Text as="p" variant="base-regular" color="secondary" className="discover-card__created">
                            {createdOn}
                        </Text>
                    ) : null}
                </div>
            </div>
            <div className="discover-card__slot">{action}</div>
            <div className="discover-card__members">
                <Text as="p" variant="base-medium" color="secondary" className="discover-card__members-label">
                    Members
                </Text>
                <MemberAvatarStack emails={stackEmails} people={people} total={workspace.member_count} />
            </div>
        </div>
    );
}
